import { z } from 'zod';
import { endpoint } from './endpoint.js';
import { OfferIdSchema } from '../schemas/offer.js';
import {
  OfferVersionIdSchema,
  OfferVersionListSchema,
  PublishVersionBodySchema,
  PublishVersionResponseSchema,
} from '../schemas/offer-version.js';
import {
  AcceptVersionBodySchema,
  AcceptVersionResponseSchema,
  ClientOfferViewResponseSchema,
  OfferVersionDiffResponseSchema,
  OfferVersionSnapshotWithEnrichmentSchema,
  RejectVersionBodySchema,
  RejectVersionResponseSchema,
  WithdrawAcceptanceBulkRequestSchema,
  WithdrawAcceptanceBulkResponseSchema,
  WithdrawalPreviewResponseSchema,
} from '../schemas/offer-version-diff.js';
import {
  LockPreviewRequestSchema,
  LockPreviewResponseSchema,
  LockReservationsRequestSchema,
  LockReservationsResponseSchema,
} from '../schemas/offer-version-lock.js';
import {
  ReleaseRejectedPreviewResponseSchema,
  ReleaseRejectedUnitsResponseSchema,
  RemoveRejectedPreviewResponseSchema,
  RemoveRejectedUnitsResponseSchema,
  UnitDecisionsListResponseSchema,
} from '../schemas/offer-version-unit-decisions.js';

export { PublishVersionBodySchema };
export type { PublishVersionBody } from '../schemas/offer-version.js';

export const offerVersions = {
  publish: endpoint(
    'POST',
    '/offers/:id/publish-version',
    z.object({ id: OfferIdSchema }).merge(PublishVersionBodySchema),
    PublishVersionResponseSchema,
  ),
  list: endpoint(
    'GET',
    '/offers/:id/versions',
    z.object({ id: OfferIdSchema }).strict(),
    OfferVersionListSchema,
  ),
  getSnapshot: endpoint(
    'GET',
    '/offers/:id/versions/:versionId',
    z.object({ id: OfferIdSchema, versionId: OfferVersionIdSchema }).strict(),
    OfferVersionSnapshotWithEnrichmentSchema,
  ),
  diff: endpoint(
    'GET',
    '/offers/:id/versions/:versionId/diff',
    z
      .object({
        id: OfferIdSchema,
        versionId: OfferVersionIdSchema,
        against: OfferVersionIdSchema,
      })
      .strict(),
    OfferVersionDiffResponseSchema,
  ),
  clientView: endpoint(
    'GET',
    '/offers/:id/client-view',
    z.object({ id: OfferIdSchema }).strict(),
    ClientOfferViewResponseSchema,
  ),
  accept: endpoint(
    'POST',
    '/offers/:id/versions/:versionId/accept',
    z.object({ id: OfferIdSchema, versionId: OfferVersionIdSchema }).merge(AcceptVersionBodySchema),
    AcceptVersionResponseSchema,
  ),
  reject: endpoint(
    'POST',
    '/offers/:id/versions/:versionId/reject',
    z.object({ id: OfferIdSchema, versionId: OfferVersionIdSchema }).merge(RejectVersionBodySchema),
    RejectVersionResponseSchema,
  ),
  /**
   * POST /offers/versions/withdraw-acceptance-bulk — staff bulk withdrawal of
   * acceptance across several offers in one call.
   * client-offer-decision-persistence / Bulk staff withdrawal of acceptance across offers
   */
  withdrawAcceptanceBulk: endpoint(
    'POST',
    '/offers/versions/withdraw-acceptance-bulk',
    WithdrawAcceptanceBulkRequestSchema,
    WithdrawAcceptanceBulkResponseSchema,
  ),
  lockReservations: endpoint(
    'POST',
    '/offers/:offerId/versions/:versionId/lock-reservations',
    z
      .object({ offerId: OfferIdSchema, versionId: OfferVersionIdSchema })
      .merge(LockReservationsRequestSchema),
    LockReservationsResponseSchema,
  ),
  /**
   * GET /offers/:offerId/versions/:versionId/lock-reservations/preview?status=...
   * (offer-accept-reservation / "Lock-reservations preview"). `status` is the
   * intended hold type — a query param (GET), not a body, because the
   * outcome differs by type and the dialog recomputes when it changes.
   */
  lockPreview: endpoint(
    'GET',
    '/offers/:offerId/versions/:versionId/lock-reservations/preview',
    z
      .object({ offerId: OfferIdSchema, versionId: OfferVersionIdSchema })
      .merge(LockPreviewRequestSchema),
    LockPreviewResponseSchema,
  ),
  unitDecisions: endpoint(
    'GET',
    '/offers/:offerId/versions/:versionId/unit-decisions',
    z.object({ offerId: OfferIdSchema, versionId: OfferVersionIdSchema }).strict(),
    UnitDecisionsListResponseSchema,
  ),
  releaseRejectedUnits: endpoint(
    'POST',
    '/offers/:offerId/versions/:versionId/release-rejected-units',
    z.object({ offerId: OfferIdSchema, versionId: OfferVersionIdSchema }).strict(),
    ReleaseRejectedUnitsResponseSchema,
  ),
  /**
   * GET /offers/:offerId/versions/:versionId/release-rejected-units/preview
   * (client-offer-decision-persistence / "Release-rejected-units preview").
   * No body — same GET-with-path-params-only style as withdrawalPreview.
   */
  releaseRejectedPreview: endpoint(
    'GET',
    '/offers/:offerId/versions/:versionId/release-rejected-units/preview',
    z.object({ offerId: OfferIdSchema, versionId: OfferVersionIdSchema }).strict(),
    ReleaseRejectedPreviewResponseSchema,
  ),
  /**
   * POST /offers/:offerId/versions/:versionId/remove-rejected-units
   * (client-offer-decision-persistence / "Manual removal of rejected units
   * from the offer draft"). Removes the draft row and, in the same per-unit
   * transaction, frees any hold the unit still carries.
   */
  removeRejectedUnits: endpoint(
    'POST',
    '/offers/:offerId/versions/:versionId/remove-rejected-units',
    z.object({ offerId: OfferIdSchema, versionId: OfferVersionIdSchema }).strict(),
    RemoveRejectedUnitsResponseSchema,
  ),
  /**
   * GET /offers/:offerId/versions/:versionId/remove-rejected-units/preview
   * (client-offer-decision-persistence / "Pre-commit breakdown in the
   * remove-rejected-units dialog"). No body — same GET-with-path-params-only
   * style as releaseRejectedPreview.
   */
  removeRejectedPreview: endpoint(
    'GET',
    '/offers/:offerId/versions/:versionId/remove-rejected-units/preview',
    z.object({ offerId: OfferIdSchema, versionId: OfferVersionIdSchema }).strict(),
    RemoveRejectedPreviewResponseSchema,
  ),
  /**
   * GET /offers/:offerId/versions/:versionId/withdrawal-preview
   * (offer-versioning-diff / "Acceptance-withdrawal preview"). No body — same
   * GET-with-path-params-only style as offerApproval.confirmationPreview.
   */
  withdrawalPreview: endpoint(
    'GET',
    '/offers/:offerId/versions/:versionId/withdrawal-preview',
    z.object({ offerId: OfferIdSchema, versionId: OfferVersionIdSchema }).strict(),
    WithdrawalPreviewResponseSchema,
  ),
};
